import { getDoc, getDocs, query, where } from 'firebase/firestore';
import { db, getTenantCollection, getTenantDoc } from '../../../firebase/config';
import { logger } from '../../../services/logger';

async function fetchCrossLinkedAccountIds(userUid) {
  try {
    const accountsRef = getTenantCollection(db, 'accounts');
    const q = query(accountsRef, where('members', 'array-contains', userUid));
    const snap = await getDocs(q);
    if (!snap.empty) {
      const otherDocs = snap.docs.filter((d) => d.id !== userUid);
      return Array.from(new Set(otherDocs.map((d) => d.id)));
    }
  } catch (crossErr) {
    logger.warn('FeedbackModal: Could not query linked accounts:', crossErr);
  }
  return [];
}

export async function fetchUserAccountWithLinkedIds(user) {
  if (!user) return null;

  try {
    const userSnap = await getDoc(getTenantDoc(db, 'accounts', user.uid));
    const userData = userSnap.exists() ? userSnap.data() : null;
    let linkedIds = [];

    if (Array.isArray(userData?.members)) {
      linkedIds = userData.members.filter((id) => id && id !== user.uid);
    }

    if (linkedIds.length === 0) {
      linkedIds = await fetchCrossLinkedAccountIds(user.uid);
    }

    return {
      id: userSnap.exists() ? userSnap.id : user.uid,
      ...(userData || {}),
      linkedAccountIds: linkedIds,
    };
  } catch (e) {
    logger.warn('FeedbackModal: Could not fetch user account:', e);
    return null;
  }
}

function resolveFirstLast(firstName, lastName) {
  if (!firstName) return '';
  return `${firstName} ${lastName || ''}`.trim();
}

function resolveAccountName(accountData) {
  if (accountData?.name) return accountData.name;
  if (accountData?.parentName) return accountData.parentName;

  const directName = resolveFirstLast(accountData?.firstName, accountData?.lastName);
  if (directName) return directName;

  return resolveFirstLast(
    accountData?.parentDetails?.firstName,
    accountData?.parentDetails?.lastName
  );
}

export function resolveFeedbackUserName(accountData, user, userEmail) {
  const accountName = resolveAccountName(accountData);
  if (accountName) return accountName;
  return user?.displayName || userEmail || '';
}

function resolveUserPhone(user, accountData) {
  return user?.phoneNumber || accountData?.phoneNumber || accountData?.phone || '';
}

export function resolveFeedbackUserDetails(accountData, user) {
  const userUid = user?.uid || '';
  const userEmail = user?.email || accountData?.email || '';
  const userPhone = resolveUserPhone(user, accountData);
  const userRole = accountData?.role || 'guardian';
  const userName = resolveFeedbackUserName(accountData, user, userEmail);

  return {
    userName,
    userEmail,
    userPhone,
    userRole,
    accountId: userUid,
    userUid,
    linkedAccountIds: accountData?.linkedAccountIds || [],
  };
}

export function resolveEnvironmentLabel() {
  const isStaging =
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('stg') ||
      window.location.hostname.includes('localhost') ||
      window.location.hostname.includes('127.0.0.1') ||
      window.location.hostname.includes('-stg'));
  return isStaging ? 'stage' : 'prod';
}
